import React from "react";
import { useLocation } from "react-router";

import {
  Box,
  ClickAwayListener,
  Divider,
  Grow,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  styled,
} from "@mui/material";

import { isMenuItemActive } from "../helpers";

const MenuDivider = styled(Divider)({
  margin: "0px !important",
});

export const MenuPopper = React.memo(
  ({
    hoveredItem,
    tooltipAnchorEl,
    handlePopperMouseEnter,
    handleMenuItemLeave,
    handleNavigate,
    globalState,
    calculateIsModuleAllowed,
  }) => {
    const { pathname } = useLocation();

    if (!hoveredItem?.children?.length || !tooltipAnchorEl) {
      return null;
    }

    return (
      <Popper
        open={true}
        anchorEl={tooltipAnchorEl}
        placement="right-start"
        transition
        disablePortal
        modifiers={[
          {
            name: "offset",
            options: {
              offset: [-50, 14],
            },
          },
        ]}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: "left top" }}>
            <Paper
              elevation={3}
              sx={{
                width: "380px",
                maxWidth: "100%",
                zIndex: 1300,
                borderRadius: "0.5rem",
                border: "1px solid",
                borderColor: "colors.borderVeryDarkGray",
                overflow: "hidden",
                py: 1.5,
                boxShadow: (theme) => theme.shadows[2],
              }}
              onMouseEnter={handlePopperMouseEnter}
              onMouseLeave={handleMenuItemLeave}
            >
              <ClickAwayListener onClickAway={handleMenuItemLeave}>
                <Box>
                  <List component="div" disablePadding>
                    <ListItem
                      sx={{
                        height: "55px",
                        pt: 0,
                        color: "primary.main",
                        '[data-mui-color-scheme="dark"] &': {
                          color: "primary.light",
                        },
                      }}
                    >
                      <ListItemIcon sx={{ mr: 1, minWidth: 32 }}>
                        <hoveredItem.icon
                          sx={{
                            color: "primary.main",
                            '[data-mui-color-scheme="dark"] &': {
                              color: "primary.light",
                            },
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText primary={hoveredItem?.text} />
                    </ListItem>
                    <MenuDivider />
                    <List component="div" disablePadding>
                      {hoveredItem?.children?.map((subItem) => {
                        const subPath = typeof subItem.path === "function" ? subItem.path(globalState) : subItem.path;
                        const isAllowed =
                          subItem.allowedModule === null
                            ? hoveredItem.id === "broker"
                              ? hoveredItem.isBrokerMenuAllowed(globalState)
                              : true
                            : subItem.allowedModule === true || calculateIsModuleAllowed(subItem.allowedModule);
                        const isDisabled = subItem.disabledCheck ? subItem.disabledCheck(globalState) : !isAllowed;
                        const isActive = isMenuItemActive(pathname, subPath);

                        return (
                          <ListItemButton
                            key={subItem.id}
                            onClick={() => {
                              handleNavigate(subPath);
                              handleMenuItemLeave();
                            }}
                            disabled={isDisabled}
                            sx={{
                              pl: isActive ? 6 : 7,
                              height: "48px",
                              color: isActive ? "secondary.main" : "inherit",
                              borderRightStyle: "solid",
                              borderRightWidth: "6px",
                              borderRightColor: isActive ? "secondary.main" : "transparent",
                              transition: "padding-left 0.2s ease-in-out",
                              "&:hover": {
                                pl: 6,
                              },
                            }}
                          >
                            <ListItemText primary={subItem.text} />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </List>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    );
  }
);
